import { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { Plus, Calendar, Clock, Repeat, ShieldCheck, Paperclip, ListChecks, AlertTriangle, Archive, EyeOff, Eye, FileMinus, X, Settings, Bell, Building2, User as UserIcon, Focus, Play, Pause, Palette, Columns3, List as ListIcon, SlidersHorizontal, ChevronDown, ArrowDownUp, Package } from "lucide-react";
import { getMe, definirTarefaAtual } from "../../services/me";
import { Avatar } from "../../components/Avatar";
import { listarFuncoesTarefa } from "../../services/funcoesTarefa";
import {
  getTarefasLembreteAtivo, setTarefasLembreteAtivo,
  getTarefasAntecedencia, setTarefasAntecedencia,
  getTarefasRepeticoes, setTarefasRepeticoes,
  getTarefasIntervalo, setTarefasIntervalo,
  TAREFA_ANTECEDENCIA_OPCOES, TAREFA_INTERVALO_OPCOES,
} from "../../hooks/useTarefasNotificacoes";
import { listarTarefas, atualizarTarefa, Tarefa } from "../../services/tarefas";
import { listarColunasKanban } from "../../services/colunasKanban";
import { registrarAjusteComAnexos } from "../../services/ajustes";
import { RegistrarAjusteModal, DadosAjuste } from "../../components/RegistrarAjusteModal";
import { listarProjetos, rotuloProjeto } from "../../services/projetos";
import { listarUsuariosAtivos } from "../../services/usuarios";
import { listarTiposProjeto } from "../../services/tiposProjeto";
import { listarTiposTarefa } from "../../services/tiposTarefa";
import { listarTags } from "../../services/tags";
import { listarClientes } from "../../services/clientes";
import { MultiSelectFiltro } from "../../components/MultiSelectFiltro";
import { TagChip } from "../../components/TagChip";
import { TipoProjetoChip } from "../../components/TipoProjetoChip";
import { useAuth } from "../../hooks/useAuth";
import { useDragToScroll } from "../../hooks/useDragToScroll";
import { KanbanTopScrollbar } from "../../components/KanbanTopScrollbar";
import { SeloCobrancaCard } from "../../components/SeloCobrancaCard";
import { urgenciaTarefa, diasRestantes, formatarPrazo } from "../../utils/urgencia";
import { avisoToast } from "../../utils/avisoToast";
import { tarefaPodeVencer } from "../../utils/tarefaVencida";
import { TarefaDrawer } from "./TarefaDrawer";
import { NovaTarefaModal } from "./NovaTarefaModal";
import { SearchInput, matchBusca } from "../../components/SearchInput";
import { SearchableSelect } from "../../components/SearchableSelect";
import { lerColunaPrefs, salvarColunaPrefs, aplicarPrefs, ColunaPrefs } from "../../hooks/useColunaPrefs";

// Persistência do toggle Externos|Internos da aba de Tarefas (espelha ProjetosList)
const KEY_TAREFAS_INTERNO = "tarefas_interno_mode";
type InternoMode = "todos" | "externos" | "internos";
function lerInternoStorage(): InternoMode {
  if (typeof window === "undefined") return "todos";
  const raw = window.localStorage.getItem(KEY_TAREFAS_INTERNO);
  return raw === "internos" || raw === "externos" || raw === "todos" ? raw : "todos";
}

/** ISO/date → "YYYY-MM-DD" local (pra value de <input type=date>). */
function ymdLocal(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * "YYYY-MM-DD" → Date no fuso LOCAL (início ou fim do dia).
 *
 * NÃO trocar por `new Date("2026-07-21")`: essa forma parseia como meia-noite
 * UTC, que no Brasil (UTC-3) é 21h do dia ANTERIOR — o filtro de período
 * perderia/ganharia um dia inteiro nas pontas. Mesmo bug já corrigido no
 * backend (`utils/periodo.ts`), aqui na versão do cliente.
 */
function diaLocal(ymd: string, fimDoDia = false): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return fimDoDia
    ? new Date(y, m - 1, d, 23, 59, 59, 999)
    : new Date(y, m - 1, d, 0, 0, 0, 0);
}

/**
 * Ordenação de VISUALIZAÇÃO dos cards dentro da coluna (pedido da Thainá,
 * jul/2026: "não tem nenhuma automatização para organizar as colunas? nome,
 * data de entrega"). "" = ordem manual (a que a pessoa montou arrastando).
 */
export type OrdenacaoKanban = "" | "prazo" | "entrega" | "titulo" | "prioridade";

export const ORDENACOES: Array<{ valor: OrdenacaoKanban; label: string }> = [
  { valor: "", label: "Ordem manual (arrastar)" },
  { valor: "prazo", label: "Prazo — mais próximo primeiro" },
  { valor: "entrega", label: "Entrega ao cliente" },
  { valor: "titulo", label: "Nome (A-Z)" },
  { valor: "prioridade", label: "Prioridade" },
];

const PESO_PRIORIDADE: Record<string, number> = { urgente: 0, alta: 1, media: 2, baixa: 3 };

/**
 * Comparador dos cards. Datas ausentes vão pro FIM (não pro começo): um card
 * sem prazo não deve encabeçar a lista de "mais próximo primeiro".
 */
function compararTarefas(a: Tarefa, b: Tarefa, modo: OrdenacaoKanban): number {
  const porData = (x?: string | null, y?: string | null) => {
    const tx = x ? new Date(x).getTime() : Infinity;
    const ty = y ? new Date(y).getTime() : Infinity;
    if (isNaN(tx) && isNaN(ty)) return 0;
    return (isNaN(tx) ? Infinity : tx) - (isNaN(ty) ? Infinity : ty);
  };
  switch (modo) {
    case "prazo":
      return porData(a.prazo, b.prazo);
    case "entrega":
      return porData(a.dataEntregaCliente, b.dataEntregaCliente);
    case "titulo":
      return (a.titulo || "").localeCompare(b.titulo || "", "pt-BR", { sensitivity: "base" });
    case "prioridade":
      return (PESO_PRIORIDADE[a.prioridade] ?? 9) - (PESO_PRIORIDADE[b.prioridade] ?? 9);
    default:
      return 0;
  }
}

export const COR_COLUNA_PADRAO = "#94a3b8"; // slate-400

/**
 * "#rrggbb" (ou "#rgb") → "rgba(r,g,b,alpha)". Usado pra acender a coluna na cor
 * dela ao arrastar um card por cima. A cor vem do seletor de cor da coluna, que
 * sempre entrega hex; qualquer coisa fora disso cai no cinza padrão em vez de
 * gerar uma cor inválida (que o browser ignoraria, sumindo com o destaque).
 */
export function hexParaRgba(hex: string, alpha: number): string {
  let h = (hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) h = COR_COLUNA_PADRAO.slice(1);
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Edição RÁPIDA de data no card (prazo).
 *
 * Histórico (importa pra não regredir):
 *  - Antes: salvava a cada `onChange` — digitar UM número já gravava e fechava
 *    (bug reportado pela Thainá em jul/2026).
 *  - Depois: passou a exigir confirmação nos botões ✓/✕.
 *  - Agora (ago/2026, pedido dela): SEM os botões — mas continua NÃO salvando a
 *    cada tecla. Grava quando a edição termina: Enter ou ao clicar fora (blur).
 *    Escape cancela. É o meio-termo: some a confirmação extra sem trazer o bug
 *    de volta, porque `onChange` só mexe no estado local.
 */
function QuickEditData({ valorInicial, salvando, onSalvar, onCancelar }: {
  valorInicial: string;
  salvando: boolean;
  onSalvar: (ymd: string) => void;
  onCancelar: () => void;
}) {
  const [valor, setValor] = useState(valorInicial);
  // Escape/salvo já tratados → o blur seguinte não pode salvar de novo.
  const encerrado = useRef(false);

  function concluir() {
    if (encerrado.current || salvando) return;
    encerrado.current = true;
    // Só grava se a data MUDOU e é válida; senão só fecha (nada de PATCH à toa).
    if (valor && valor !== valorInicial) onSalvar(valor);
    else onCancelar();
  }

  return (
    <span
      className="inline-flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        type="date"
        autoFocus
        value={valor}
        onChange={(e) => setValor(e.target.value)}   // só estado local — nunca salva aqui
        onBlur={concluir}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") { e.preventDefault(); concluir(); }
          else if (e.key === "Escape") {
            e.preventDefault();
            encerrado.current = true;   // impede o blur de salvar depois do cancelar
            onCancelar();
          }
        }}
        title="Escolha a data e clique fora (ou Enter) para salvar · Esc cancela"
        className="text-[11px] px-1 py-0.5 rounded border border-blue-300 outline-none dark:bg-slate-900"
      />
    </span>
  );
}

/**
 * Cor do avatar quando a pessoa não tem foto. Determinística pelo id, pra mesma
 * pessoa ter sempre a mesma cor em todos os cards (dá pra reconhecer de relance).
 */
const CORES_AVATAR = [
  "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-violet-500",
  "bg-rose-500", "bg-cyan-500", "bg-orange-500", "bg-teal-500",
];
function corDaPessoa(id: string) {
  let soma = 0;
  for (let i = 0; i < id.length; i++) soma = (soma + id.charCodeAt(i)) % 997;
  return CORES_AVATAR[soma % CORES_AVATAR.length];
}

/**
 * Edição rápida de QUEM FAZ a tarefa, direto no card. Antes era um <select> que
 * só trocava a pessoa PRINCIPAL — os co-responsáveis nem apareciam (relato da
 * Thainá, ago/2026: "só aparece o principal, seria bom colocar os outros que
 * fazem parte"). Agora abre um painelzinho com TODOS: a principal em destaque,
 * as demais com ★ (promover) e × (tirar), mais um seletor pra incluir gente.
 * Salva `atribuidoA` + `responsaveis`, igual ao formulário da tarefa.
 */
function QuickEditPessoas({ tarefa, usuarios, salvando, ancora, onSalvar, onFechar }: {
  tarefa: Tarefa;
  usuarios: Array<{ id: string; nome: string; fotoUrl?: string | null; funcaoPadrao?: string | null }>;
  salvando: boolean;
  /** Retângulo do nome clicado — o painel é FIXO e ancorado nele. */
  ancora: DOMRect | null;
  onSalvar: (dados: {
    atribuidoA: string;
    funcaoPrincipal: string | null;
    responsaveis: Array<{ usuarioId: string; funcao: string | null }>;
  }) => void;
  onFechar: () => void;
}) {
  // Uma lista só, na ordem em que aparece na tela: o índice 0 É a principal.
  // Guardar "principal + outros" separados obrigava a sincronizar dois estados
  // a cada ★, e a função da pessoa se perdia na troca.
  type Alocado = { id: string; funcao: string | null };
  const [lista, setLista] = useState<Alocado[]>(() => {
    const inicial: Alocado[] = tarefa.atribuidoA
      ? [{ id: tarefa.atribuidoA, funcao: tarefa.funcaoPrincipal || null }]
      : [];
    for (const r of tarefa.responsaveis || []) {
      if (r.usuarioId !== tarefa.atribuidoA) inicial.push({ id: r.usuarioId, funcao: r.funcao || null });
    }
    return inicial;
  });
  const caixa = useRef<HTMLDivElement>(null);

  // Catálogo de funções (Admin → Funções na tarefa). Cache longo: muda pouco.
  const { data: funcoes = [] } = useQuery({
    queryKey: ["funcoes-tarefa"],
    queryFn: () => listarFuncoesTarefa(),
    staleTime: 5 * 60 * 1000,
  });

  // Fecha ao clicar fora (salvando o que foi mexido).
  useEffect(() => {
    function fora(e: MouseEvent) {
      if (caixa.current && !caixa.current.contains(e.target as Node)) aplicar();
    }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  });

  /** Assinatura da alocação — compara pessoas E funções, na ordem (a ordem importa: [0] é a principal). */
  function assinatura(l: Alocado[]) {
    return l.map((a) => `${a.id}:${a.funcao || ""}`).join("|");
  }
  const original = assinatura([
    ...(tarefa.atribuidoA ? [{ id: tarefa.atribuidoA, funcao: tarefa.funcaoPrincipal || null }] : []),
    ...(tarefa.responsaveis || [])
      .filter((r) => r.usuarioId !== tarefa.atribuidoA)
      .map((r) => ({ id: r.usuarioId, funcao: r.funcao || null })),
  ]);

  function aplicar() {
    const principal = lista[0];
    if (principal && assinatura(lista) !== original) {
      onSalvar({
        atribuidoA: principal.id,
        funcaoPrincipal: principal.funcao,
        responsaveis: lista.slice(1).map((a) => ({ usuarioId: a.id, funcao: a.funcao })),
      });
    } else onFechar();
  }

  function tornarPrincipal(id: string) {
    setLista((l) => {
      const alvo = l.find((a) => a.id === id);
      return alvo ? [alvo, ...l.filter((a) => a.id !== id)] : l;
    });
  }
  function tirar(id: string) {
    setLista((l) => l.filter((a) => a.id !== id));
  }
  function definirFuncao(id: string, funcao: string) {
    setLista((l) => l.map((a) => (a.id === id ? { ...a, funcao: funcao || null } : a)));
  }

  const nome = (id: string) => usuarios.find((u) => u.id === id)?.nome || id;
  const fotoDe = (id: string) => usuarios.find((u) => u.id === id)?.fotoUrl || null;

  // Posição FIXA ancorada no nome. Precisa ser fixa + portal: a coluna do kanban
  // rola, então um painel `absolute` dentro do card era cortado (a lista de
  // pessoas sumia pra fora da coluna).
  const LARGURA = 288;
  const [altura, setAltura] = useState(0);
  useEffect(() => { setAltura(caixa.current?.offsetHeight || 0); }, [lista.length]);
  const r = ancora;
  const cabeAbaixo = !r || r.bottom + altura + 8 <= window.innerHeight || r.top - altura - 8 < 0;
  const estilo: React.CSSProperties = r
    ? {
        position: "fixed",
        width: LARGURA,
        left: Math.max(8, Math.min(r.left, window.innerWidth - LARGURA - 8)),
        top: cabeAbaixo ? r.bottom + 4 : r.top - altura - 4,
      }
    : { position: "fixed", width: LARGURA, left: 8, top: 8 };

  return createPortal(
    <div
      ref={caixa}
      style={estilo}
      className="z-[60] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-2"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Quem vai fazer</p>
      <div className="flex flex-col gap-1.5 mb-2">
        {lista.length === 0 && <span className="text-[11px] text-slate-400 italic">Ninguém ainda.</span>}
        {lista.map((a, i) => {
          const ehPrincipal = i === 0;
          return (
            <div key={a.id} className="flex items-center gap-1.5">
              <Avatar nome={nome(a.id)} fotoUrl={fotoDe(a.id)} tamanho={24} bgFallback={corDaPessoa(a.id)} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <span className="text-[11px] font-medium truncate text-slate-700 dark:text-slate-200">{nome(a.id)}</span>
                  {ehPrincipal && (
                    <span className="text-[8px] uppercase font-bold bg-blue-600 text-white px-1 py-px rounded shrink-0">1ª</span>
                  )}
                </div>
                {/* A função é do par pessoa+card: a mesma pessoa pode editar num
                    card e captar em outro. Catálogo em Admin → Funções na tarefa. */}
                <select
                  value={a.funcao || ""}
                  onChange={(e) => definirFuncao(a.id, e.target.value)}
                  className="w-full text-[10px] px-1 py-0.5 mt-0.5 rounded border border-slate-200 dark:border-slate-600 dark:bg-slate-900 text-slate-600 dark:text-slate-300 outline-none"
                >
                  <option value="">— sem função</option>
                  {funcoes.map((f) => (
                    <option key={f.id} value={f.nome}>{f.nome}</option>
                  ))}
                  {/* Função legada (saiu do catálogo) continua visível, não some do card. */}
                  {a.funcao && !funcoes.some((f) => f.nome === a.funcao) && (
                    <option value={a.funcao}>{a.funcao} (fora do catálogo)</option>
                  )}
                </select>
              </div>
              <div className="flex flex-col shrink-0">
                {!ehPrincipal && (
                  <button type="button" onClick={() => tornarPrincipal(a.id)} title="Tornar principal"
                    className="text-slate-400 hover:text-blue-600 leading-none text-[11px]">★</button>
                )}
                <button type="button" onClick={() => tirar(a.id)} title="Tirar da tarefa"
                  className="text-slate-400 hover:text-red-600 leading-none">
                  <X size={11} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <select
        value=""
        onChange={(e) => {
          const id = e.target.value;
          if (!id) return;
          // Já entra com a FUNÇÃO PADRÃO do cadastro da pessoa (Thainá, ago/2026);
          // o select da linha dela continua editável pra trocar quando variar.
          const padrao = usuarios.find((u) => u.id === id)?.funcaoPadrao || null;
          setLista((l) => (l.some((a) => a.id === id) ? l : [...l, { id, funcao: padrao }]));
        }}
        className="w-full text-[11px] px-1.5 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-900 outline-none"
      >
        <option value="">+ adicionar pessoa…</option>
        {usuarios.filter((u) => !lista.some((a) => a.id === u.id)).map((u) => (
          <option key={u.id} value={u.id}>{u.nome}</option>
        ))}
      </select>
      {funcoes.length === 0 && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1.5 leading-tight">
          Nenhuma função cadastrada ainda — crie em <strong>Admin → Funções na tarefa</strong>.
        </p>
      )}
      <div className="flex justify-end gap-1.5 mt-2">
        <button type="button" onClick={onFechar} className="text-[11px] px-2 py-1 rounded bg-slate-100 dark:bg-slate-700 hover:bg-slate-200">Cancelar</button>
        <button type="button" onClick={aplicar} disabled={salvando || lista.length === 0}
          className="text-[11px] px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
          Salvar
        </button>
      </div>
    </div>,
    document.body,
  );
}

export function TarefasKanban() {
  const { user, can } = useAuth();
  const ehSuporte = can(["suporte"]);
  const ehGestao = can(["gestor", "admin"]);
  const qc = useQueryClient();
  // ESCOPO PADRÃO POR CARGO (pedido do Ricardo, ago/2026): gestor/admin abre a
  // tela já vendo as tarefas de TODO MUNDO — é o trabalho deles. Funcionário
  // continua abrindo em "Minhas", que é o que ele precisa ver primeiro.
  // A escolha de cada um é lembrada: quem clicar no outro botão mantém.
  const [escopo, setEscopo] = useState<"minhas" | "todas">(() => {
    try {
      const salvo = localStorage.getItem("tarefas_escopo");
      if (salvo === "minhas" || salvo === "todas") return salvo;
    } catch { /* sem localStorage: cai no padrão */ }
    return "minhas";
  });
  const escopoEscolhido = useRef<boolean>((() => {
    try { return localStorage.getItem("tarefas_escopo") !== null; } catch { return false; }
  })());
  // O perfil pode chegar DEPOIS da 1ª renderização (auth ainda carregando), e aí
  // o valor inicial acima teria caído em "minhas" mesmo pra um gestor. Este
  // efeito corrige quando o cargo aparece — mas só se a pessoa nunca escolheu.
  useEffect(() => {
    if (!escopoEscolhido.current && ehGestao) setEscopo("todas");
  }, [ehGestao]);
  function trocarEscopo(novo: "minhas" | "todas") {
    escopoEscolhido.current = true;
    setEscopo(novo);
    try { localStorage.setItem("tarefas_escopo", novo); } catch { /* ignora */ }
  }
  const [vista, setVista] = useState<"kanban" | "lista">(() => (typeof window !== "undefined" && localStorage.getItem("tarefas_vista") === "lista" ? "lista" : "kanban"));
  useEffect(() => { try { localStorage.setItem("tarefas_vista", vista); } catch {} }, [vista]);
  // MELHORIA 24 — cada pessoa liga/desliga os detalhes na capa do card (só no
  // navegador dela; default LIGADO). Desligado = card enxuto: título + prazo +
  // responsável; esconde tipo, projeto, peças, selos, tags, anexos, subitens.
  const [detalhesCards, setDetalhesCards] = useState<boolean>(() => {
    try { return localStorage.getItem("tarefas_detalhes_cards") !== "0"; } catch { return true; }
  });
  useEffect(() => { try { localStorage.setItem("tarefas_detalhes_cards", detalhesCards ? "1" : "0"); } catch {} }, [detalhesCards]);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [showNova, setShowNova] = useState(false);
  const [showConfigNotif, setShowConfigNotif] = useState(false);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const { data: colunasGlobais = [] } = useQuery({
    queryKey: ["colunas-kanban", "ativas"],
    queryFn: () => listarColunasKanban(),
  });
  // Preferências pessoais do quadro (ordem + cor das colunas) — Viz #3 / #4.
  const [colPrefs, setColPrefs] = useState<ColunaPrefs>(() => lerColunaPrefs(user?.id));
  useEffect(() => { setColPrefs(lerColunaPrefs(user?.id)); }, [user?.id]);
  function atualizarPrefs(p: ColunaPrefs) { setColPrefs(p); salvarColunaPrefs(user?.id, p); }
  const colunasBase = colunasGlobais.map((c) => ({ id: c.chave, titulo: c.label, cor: c.cor }));
  const colunas = useMemo(() => aplicarPrefs(colunasBase, colPrefs), [colunasGlobais, colPrefs]);
  const [busca, setBusca] = useState("");
  // Painel de filtros recolhível (pedido da Rec, jul/2026 — os filtros poluíam a
  // tela). Fica escondido atrás do botão "Filtros"; o estado aberto/fechado é
  // lembrado por navegador.
  const [showFiltros, setShowFiltros] = useState<boolean>(() => {
    try { return localStorage.getItem("tarefas_filtros_abertos") === "1"; } catch { return false; }
  });
  function toggleFiltros() {
    setShowFiltros((v) => {
      const novo = !v;
      try { localStorage.setItem("tarefas_filtros_abertos", novo ? "1" : "0"); } catch {}
      return novo;
    });
  }
  const [searchParams, setSearchParams] = useSearchParams();
  const [mostrarArquivadas, setMostrarArquivadas] = useState(false);
  const [filtroProjetoId, setFiltroProjetoId] = useState<string>("");
  // MÚLTIPLA escolha (jul/2026, pedido da Thainá): antes eram um valor só cada.
  // `filtroTipo` = categoria do PROJETO (Mensal/Evento/…). `filtroTiposTarefa` =
  // tipo da TAREFA (Arte digital/Impresso/…) — coisas diferentes, e o segundo
  // simplesmente não existia, que era a dúvida dela.
  const [filtroAtribuidos, setFiltroAtribuidos] = useState<string[]>([]);
  const [filtroTiposTarefa, setFiltroTiposTarefa] = useState<string[]>([]);
  const [filtroTipo, setFiltroTipo] = useState<string>("");
  // MELHORIA 14 — modalidade do PROJETO (avulso/mensal), que é o campo real do
  // "avulso/mensal" (a categoria `tipo` quase sempre é "dev" e não servia). Tarefa
  // sem projeto não tem modalidade e some quando esse filtro está ligado.
  const [filtroModalidade, setFiltroModalidade] = useState<string>("");
  const [filtroTag, setFiltroTag] = useState<string>("");
  // Período sobre o PRAZO da tarefa (date-only, "YYYY-MM-DD").
  const [filtroDataDe, setFiltroDataDe] = useState<string>("");
  const [filtroDataAte, setFiltroDataAte] = useState<string>("");
  // Cliente vem do projeto pai (projeto.clienteRef). Tarefa avulsa ou de projeto
  // interno não tem cliente e some quando esse filtro está ligado.
  const [filtroClientes, setFiltroClientes] = useState<string[]>([]);
  // Ordenação é só de VISUALIZAÇÃO: reorganiza a tela e NÃO grava nada. A ordem
  // manual (ordemKanban, feita arrastando) continua intacta e volta assim que
  // isso for pra "Manual". Decisão do Ricardo (21/jul/2026) — ordenação que
  // gravasse tornaria o arrastar dentro da coluna inútil.
  const [ordenarPor, setOrdenarPor] = useState<OrdenacaoKanban>("");
  // Toggle Todos | Externos | Internos. Suporte fica travado em internos. Persistido.
  const [internoMode, setInternoMode] = useState<InternoMode>(() =>
    ehSuporte ? "internos" : lerInternoStorage(),
  );
  useEffect(() => { try { localStorage.setItem(KEY_TAREFAS_INTERNO, internoMode); } catch {} }, [internoMode]);
  // Chips de filtro rápido — todos opcionais, off por default
  const [apenasInternas, setApenasInternas] = useState(false);
  const [apenasVisiveisCliente, setApenasVisiveisCliente] = useState(false);
  const [apenasAvulsas, setApenasAvulsas] = useState(false);
  const [apenasAtrasadas, setApenasAtrasadas] = useState(false);
  // "Vencem em 24h" — espelha o card da dashboard: prazo entre hoje 00:00 e +24h,
  // só status que ainda podem vencer. Usado pelo deep-link ?vence24h=1.
  const [apenasVence24h, setApenasVence24h] = useState(false);
  // Recorte por COLUNA/status (deep-link ?status=aguardando_cliente, do card
  // "Aguardando cliente" da dashboard). "" = sem recorte.
  const [filtroColuna, setFiltroColuna] = useState<string>("");
  // "Próprias" = só onde o usuário é o ATRIBUÍDO (mais restritivo que "Minhas",
  // que já inclui tarefas onde ele é criador/aprovador mas atribuídas a outros).
  const [apenasProprias, setApenasProprias] = useState(false);

  const { data: projetosData } = useQuery({
    queryKey: ["projetos-list-filtro"],
    queryFn: () => listarProjetos({ perPage: 200, interno: "all" }),
  });
  const { data: usuariosAtivos = [] } = useQuery({
    queryKey: ["usuarios-ativos-filtro"],
    queryFn: listarUsuariosAtivos,
  });
  const { data: tiposProjeto = [] } = useQuery({
    queryKey: ["tipos-projeto", "ativos"],
    queryFn: () => listarTiposProjeto(),
  });
  const { data: tagsCatalog = [] } = useQuery({
    queryKey: ["tags", "ativas"],
    queryFn: () => listarTags(),
  });
  const { data: tiposTarefa = [] } = useQuery({
    queryKey: ["tipos-tarefa", "ativos"],
    queryFn: () => listarTiposTarefa(),
  });
  const { data: clientes = [] } = useQuery({
    queryKey: ["clientes-list-filtro"],
    queryFn: listarClientes,
  });
  const projetosTodos: Array<{ id: string; nome: string; interno?: boolean }> = (projetosData as any)?.projetos || [];
  // Mantém só os projetos do modo selecionado no dropdown — evita IDs órfãos
  const projetos = useMemo(
    () => projetosTodos.filter((p) =>
      internoMode === "internos" ? p.interno === true : p.interno !== true,
    ),
    [projetosTodos, internoMode],
  );
  // Se o projeto selecionado some por trocar de modo, limpa o filtro
  useEffect(() => {
    if (filtroProjetoId && !projetos.some((p) => p.id === filtroProjetoId)) {
      setFiltroProjetoId("");
    }
  }, [internoMode, projetos, filtroProjetoId]);

  // Deep-link via ?tarefa=<id> (vindo de notificação)
  useEffect(() => {
    const tid = searchParams.get("tarefa");
    if (tid && tid !== drawerId) {
      setDrawerId(tid);
      setEscopo("todas");
      setMostrarArquivadas(true); // garante que aparece se for arquivada
    }
  }, [searchParams]);

  // Deep-link dos cards da DASHBOARD (jul/2026): a pessoa clica "Vencidas",
  // "Vencem em 24h" ou "Minhas tarefas" e chega aqui já filtrada. Roda UMA vez
  // (mount) — não a cada mudança de searchParams — pra não reativar o filtro
  // quando ela depois desligar o chip manualmente. Abre o painel pra ela ver o
  // que está aplicado, e limpa os params da URL depois (assim um F5 não trava
  // no filtro e o "limpar" volta ao normal).
  const deepLinkAplicado = useRef(false);
  useEffect(() => {
    if (deepLinkAplicado.current) return;
    deepLinkAplicado.current = true;
    const p = searchParams;
    const proprias = p.get("proprias") === "1";
    const atrasadas = p.get("atrasadas") === "1";
    const vence24h = p.get("vence24h") === "1";
    const escopoTodas = p.get("escopo") === "todas";
    const status = p.get("status") || "";
    if (!proprias && !atrasadas && !vence24h && !escopoTodas && !status) return;
    if (proprias) setApenasProprias(true);
    if (atrasadas) setApenasAtrasadas(true);
    if (vence24h) setApenasVence24h(true);
    if (escopoTodas) setEscopo("todas");
    if (status) setFiltroColuna(status);
    setShowFiltros(true);
    // tira os params (mantém ?tarefa= se existir) sem recarregar
    const limpo = new URLSearchParams(p);
    ["proprias", "atrasadas", "vence24h", "escopo", "status"].forEach((k) => limpo.delete(k));
    setSearchParams(limpo, { replace: true });
  }, []);

  const { data: tarefas = [] } = useQuery({
    queryKey: ["tarefas", { escopo, mostrarArquivadas }],
    queryFn: () => listarTarefas({
      escopo: escopo === "minhas" ? "minhas" : undefined,
      arquivadas: mostrarArquivadas,
    }),
    refetchInterval: 60000,
  });

  const moverM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Tarefa> }) => atualizarTarefa(id, dados),
    // Otimista: aplica a mudança no cache na hora (sem delay ao mover entre colunas);
    // reconcilia/desfaz conforme o servidor responder.
    onMutate: async ({ id, dados }) => {
      await qc.cancelQueries({ queryKey: ["tarefas"] });
      const anteriores = qc.getQueriesData({ queryKey: ["tarefas"] });
      qc.setQueriesData({ queryKey: ["tarefas"] }, (old: any) =>
        Array.isArray(old) ? old.map((t: any) => (t.id === id ? { ...t, ...dados } : t)) : old,
      );
      return { anteriores };
    },
    onError: (_e, _vars, ctx: any) => {
      ctx?.anteriores?.forEach(([key, data]: any) => qc.setQueryData(key, data));
    },
    // Reconcilia com a resposta do servidor SEM invalidar/refetch — evita o card
    // "pular de volta" por corrida com refetchInterval/SSE durante o move.
    onSuccess: (serverTask: any, variables: any) => {
      if (!serverTask?.id) return;
      qc.setQueriesData({ queryKey: ["tarefas"] }, (old: any) =>
        Array.isArray(old) ? old.map((t: any) => (t.id === serverTask.id ? { ...t, ...serverTask } : t)) : old,
      );
      // Funcionário arrastou card-com-aprovador pra Finalizados → backend desviou
      // pra "Aprovação interna". Avisa, senão o card só "pula de coluna" sem explicação.
      if (variables?.dados?.status === "concluido" && serverTask.status === "aguardando_aprovacao") {
        avisoToast("Enviado para Aprovação interna", `"${serverTask.titulo}" precisa da aprovação de um gestor/admin para ser finalizada.`, serverTask.id);
      }
    },
  });

  // Play/Pause direto no card (PDF Fase 02 — Cards #5). Liga/desliga o foco
  // ("estou nessa") sem abrir o drawer. Ligar move a tarefa pra "em andamento"
  // (automação no backend), então revalida tarefas + me.
  const playM = useMutation({
    mutationFn: (tarefaId: string | null) => definirTarefaAtual(tarefaId),
    // Otimista: o ícone Play↔Pause troca na hora (sem esperar 2 round-trips).
    onMutate: async (tarefaId) => {
      await qc.cancelQueries({ queryKey: ["me"] });
      const anterior = qc.getQueryData(["me"]);
      qc.setQueryData(["me"], (old: any) =>
        old ? { ...old, tarefaAtualId: tarefaId, tarefaAtualDesde: tarefaId ? new Date().toISOString() : null } : old,
      );
      return { anterior };
    },
    onError: (e: any, _v, ctx: any) => {
      if (ctx?.anterior !== undefined) qc.setQueryData(["me"], ctx.anterior);
      alert(e?.response?.data?.error?.message || "Não foi possível alterar o play. Tente de novo.");
    },
    onSuccess: (res: any) => {
      qc.setQueryData(["me"], (old: any) =>
        old ? { ...old, tarefaAtualId: res?.tarefaAtualId ?? null, tarefaAtualDesde: res?.tarefaAtualDesde ?? null, tarefaAtual: res?.tarefaAtual ?? null } : old,
      );
      // O play move o card pra "em andamento" no backend — revalida o quadro.
      qc.invalidateQueries({ queryKey: ["tarefas"] });
    },
  });

  // Edição rápida no card (PDF Fase 02 — Cards #5): data e pessoa direto no quadro.
  // Só um card edita por vez. stopPropagation evita abrir o drawer / iniciar drag.
  const [cardEdit, setCardEdit] = useState<{ id: string; campo: "data" | "pessoa" } | null>(null);
  // Onde o painel de pessoas deve aparecer (retângulo do nome clicado).
  const [ancoraPessoa, setAncoraPessoa] = useState<DOMRect | null>(null);
  const quickEditM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, any> }) => atualizarTarefa(id, dados),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tarefas"] }),
  });

  // Quando arrasta pra coluna "Ajustes", abre modal pra capturar categoria+motivo
  // antes de mover. Guarda o movimento pendente até confirmar.
  const [ajustePendente, setAjustePendente] = useState<{ id: string; dados: Partial<Tarefa> } | null>(null);
  const [salvandoAjuste, setSalvandoAjuste] = useState(false);
  async function confirmarAjuste(d: DadosAjuste) {
    if (!ajustePendente) return;
    setSalvandoAjuste(true);
    try {
      // Move PRIMEIRO; só registra o ajuste se o move deu certo — evita "ajuste
      // fantasma" (registrado pra um card que rolou de volta por falha no move).
      await moverM.mutateAsync(ajustePendente);
      const { arquivos, ...dadosAjuste } = d;
      const { falhas } = await registrarAjusteComAnexos(ajustePendente.id, dadosAjuste, arquivos);
      setAjustePendente(null); // fecha só no sucesso
      if (falhas.length) alert(`Ajuste registrado, mas ${falhas.length} anexo(s) não subiram: ${falhas.join(", ")}. Reabra o card e use "+ arquivo".`);
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || "Não foi possível mover o card / registrar o ajuste. Tente de novo.");
      // mantém o modal aberto pra retry
    } finally {
      setSalvandoAjuste(false);
    }
  }

  // O auto-scroll ao arrastar perto das bordas é da PRÓPRIA lib de drag (ela rola
  // o container do droppable). Já teve um hook nosso aqui pra isso; ele rolava o
  // quadro por fora e a lib perdia as medidas → o card voltava pra origem ou caía
  // na coluna errada. Agora o quadro é o único scroll container e ela cuida disso.
  const boardRef = useRef<HTMLDivElement | null>(null);
  // Arrastar o fundo (espaço entre colunas) pra rolar de lado, sem pegar card.
  const pan = useDragToScroll(boardRef);

  function onDragEnd(result: DropResult) {
    if (!result.destination) return;
    const { draggableId, source, destination } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Reordenar COLUNAS (drag horizontal) — preferência pessoal, Viz #4.
    if (result.type === "COLUMN") {
      const novaOrdem = colunas.map((c) => c.id);
      const [movida] = novaOrdem.splice(source.index, 1);
      novaOrdem.splice(destination.index, 0, movida);
      atualizarPrefs({ ...colPrefs, ordem: novaOrdem });
      return;
    }

    const novoStatus = destination.droppableId;
    const tarefa = tarefas.find((t) => t.id === draggableId);
    if (!tarefa) return;

    // COM ORDENAÇÃO LIGADA a posição na tela é calculada, não escolhida pela
    // pessoa — então NÃO se mexe em `ordemKanban`. Sem esse desvio teria bug:
    // `destination.index` se refere à lista ORDENADA, enquanto os vizinhos são
    // lidos de `filtradas` (não ordenada), e o card cairia num ponto aleatório
    // da ordem manual. Mover ENTRE colunas continua valendo (muda o status);
    // reordenar DENTRO da coluna vira no-op, e o card volta pro lugar.
    if (ordenarPor) {
      if (tarefa.status === novoStatus) return;
      const dados: Partial<Tarefa> = { status: novoStatus };
      if (novoStatus === "ajustes") {
        setAjustePendente({ id: draggableId, dados });
        return;
      }
      moverM.mutate({ id: draggableId, dados });
      return;
    }

    // Calcula a nova ordemKanban com base nos vizinhos no destino. Trabalha
    // sobre `filtradas` (o que o usuário enxerga ordenado) — recalcular a
    // partir da lista bruta daria pra confundir o cálculo quando há filtros.
    const colunaDest = filtradas.filter((t) => t.status === novoStatus && t.id !== draggableId);
    const acima = colunaDest[destination.index - 1];
    const abaixo = colunaDest[destination.index];
    let novaOrdem: number;
    if (!acima && !abaixo) novaOrdem = 0;
    else if (!acima) novaOrdem = (abaixo!.ordemKanban ?? 0) - 1000;
    else if (!abaixo) novaOrdem = (acima.ordemKanban ?? 0) + 1000;
    else novaOrdem = ((acima.ordemKanban ?? 0) + (abaixo.ordemKanban ?? 0)) / 2;

    const dados: Partial<Tarefa> = { ordemKanban: novaOrdem };
    if (tarefa.status !== novoStatus) dados.status = novoStatus;
    // Entrando na coluna "Ajustes": captura categoria+motivo antes de mover.
    if (novoStatus === "ajustes" && tarefa.status !== "ajustes") {
      setAjustePendente({ id: draggableId, dados });
      return;
    }
    moverM.mutate({ id: draggableId, dados });
  }

  const filtradas = useMemo(() => {
    let lista = tarefas;
    // Filtro Todos | Externos | Internos pelo projeto vinculado.
    // - "todos": sem filtro (default).
    // - "internos": só tarefas de projetos internos.
    // - "externos": tarefas de projetos externos + avulsas (sem projeto).
    if (internoMode === "internos") {
      lista = lista.filter((t) => t.projeto?.interno === true);
    } else if (internoMode === "externos") {
      lista = lista.filter((t) => !t.projeto || t.projeto.interno !== true);
    }
    if (filtroProjetoId) lista = lista.filter((t) => t.projetoId === filtroProjetoId);
    // Multi: OU entre os marcados (Fulano OU Ciclano), como a Thainá pediu
    // ("selecionar mais de um colaborador para ver as tarefas").
    if (filtroAtribuidos.length > 0) {
      lista = lista.filter((t) => filtroAtribuidos.includes(t.atribuidoA));
    }
    // Idem pro tipo da TAREFA: "arte digital e impressa juntas".
    // Tarefa SEM tipo preenchido não casa com nenhum filtro — some da tela de
    // propósito (são ~80 hoje); quem quer ver tudo é só não filtrar.
    if (filtroTiposTarefa.length > 0) {
      lista = lista.filter((t) => !!t.tipoTarefaId && filtroTiposTarefa.includes(t.tipoTarefaId));
    }
    // Cliente vem do PROJETO pai. Avulsa/interna (sem cliente) não casa.
    if (filtroClientes.length > 0) {
      lista = lista.filter((t) => {
        const cid = t.projeto?.clienteRef?.id;
        return !!cid && filtroClientes.includes(cid);
      });
    }
    if (filtroDataDe) lista = lista.filter((t) => new Date(t.prazo) >= diaLocal(filtroDataDe));
    if (filtroDataAte) lista = lista.filter((t) => new Date(t.prazo) <= diaLocal(filtroDataAte, true));
    if (filtroTipo) {
      // Categoria vem do projeto pai. Tarefas avulsas (sem projeto) não casam com filtro de categoria.
      lista = lista.filter((t) => t.projeto?.tipo === filtroTipo);
    }
    if (filtroModalidade) {
      // Modalidade (avulso/mensal) vem do projeto. Tarefa sem projeto não casa.
      lista = lista.filter((t) => (t.projeto as any)?.modalidade === filtroModalidade);
    }
    if (filtroTag) {
      const alvo = filtroTag.toLowerCase();
      lista = lista.filter((t) =>
        (t.etiquetas || "").toLowerCase().split(/[,\s]+/).filter(Boolean).includes(alvo),
      );
    }
    if (apenasProprias && user?.id) lista = lista.filter((t) => t.atribuidoA === user.id);
    if (apenasInternas) lista = lista.filter((t) => !t.visivelParaCliente);
    if (apenasVisiveisCliente) lista = lista.filter((t) => !!t.visivelParaCliente);
    if (apenasAvulsas) lista = lista.filter((t) => !t.projetoId);
    if (apenasAtrasadas) {
      const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
      lista = lista.filter((t) => tarefaPodeVencer(t.status) && new Date(t.prazo) < hoje);
    }
    if (apenasVence24h) {
      // Mesma janela do card "Vencem em 24h" da dashboard: hoje 00:00 → +24h.
      const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
      const em24h = new Date(hoje.getTime() + 24 * 60 * 60 * 1000);
      lista = lista.filter((t) => {
        const p = new Date(t.prazo);
        return tarefaPodeVencer(t.status) && p >= hoje && p <= em24h;
      });
    }
    // Recorte por coluna (deep-link ?status=): só tarefas naquele status.
    if (filtroColuna) lista = lista.filter((t) => t.status === filtroColuna);

    // Suporte a #tag direto na busca: extrai tokens #xxx e filtra por eles
    // (match parcial no slug — `#pod` casa com `podcast`). O resto do texto
    // ainda é busca normal por título/descrição/projeto/etc.
    const tagTokens: string[] = [];
    const restoBusca = (busca || "")
      .split(/\s+/)
      .filter((token) => {
        if (token.startsWith("#") && token.length > 1) {
          tagTokens.push(token.slice(1).toLowerCase());
          return false;
        }
        return true;
      })
      .join(" ");

    if (tagTokens.length > 0) {
      lista = lista.filter((t) => {
        const tags = (t.etiquetas || "").toLowerCase().split(/[,\s]+/).filter(Boolean);
        return tagTokens.every((alvo) => tags.some((tag) => tag.includes(alvo)));
      });
    }

    // O CLIENTE entra na busca junto com o projeto (Thainá, ago/2026): ela pensa
    // por cliente, e procurar "Nesme" tinha que achar as tarefas do projeto dele
    // mesmo que o nome do projeto não diga o cliente. Mesma ideia do rótulo
    // "Projeto · Cliente" no seletor (ver rotuloProjeto).
    return matchBusca(lista, restoBusca, [
      (t) => t.titulo,
      (t) => t.descricao,
      (t) => t.projeto?.nome,
      (t) => t.projeto?.clienteRef?.nome,
      (t) => t.atribuido?.nome,
      (t) => t.etiquetas,
    ]);
  }, [tarefas, busca, internoMode, filtroProjetoId, filtroAtribuidos, filtroTiposTarefa, filtroClientes, filtroDataDe, filtroDataAte, filtroTipo, filtroModalidade, filtroTag, apenasProprias, apenasInternas, apenasVisiveisCliente, apenasAvulsas, apenasAtrasadas, apenasVence24h, filtroColuna, user?.id]);

  // Lista de tags únicas extraídas das tarefas atualmente carregadas — alimenta o filtro
  const todasTags = useMemo(() => {
    const set = new Set<string>();
    for (const t of tarefas) {
      if (!t.etiquetas) continue;
      for (const tag of t.etiquetas.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean)) {
        set.add(tag);
      }
    }
    return Array.from(set).sort();
  }, [tarefas]);

  // Multi conta 1 quando tem alguma coisa marcada (não 1 por item) — senão o
  // badge "3 filtros" apareceria só por escolher 3 pessoas no mesmo filtro.
  const filtrosAtivos =
    (apenasProprias ? 1 : 0) + (apenasInternas ? 1 : 0) + (apenasVisiveisCliente ? 1 : 0) + (apenasAvulsas ? 1 : 0) +
    (apenasAtrasadas ? 1 : 0) + (apenasVence24h ? 1 : 0) + (filtroColuna ? 1 : 0) + (filtroProjetoId ? 1 : 0) + (filtroAtribuidos.length > 0 ? 1 : 0) +
    (filtroTiposTarefa.length > 0 ? 1 : 0) + (filtroClientes.length > 0 ? 1 : 0) +
    (filtroDataDe || filtroDataAte ? 1 : 0) +
    (filtroTipo ? 1 : 0) + (filtroModalidade ? 1 : 0) + (filtroTag ? 1 : 0) + (mostrarArquivadas ? 1 : 0);

  function limparFiltros() {
    setApenasProprias(false);
    setApenasInternas(false);
    setApenasVisiveisCliente(false);
    setApenasAvulsas(false);
    setApenasAtrasadas(false);
    setApenasVence24h(false);
    setFiltroColuna("");
    setFiltroProjetoId("");
    setFiltroAtribuidos([]);
    setFiltroTiposTarefa([]);
    setFiltroClientes([]);
    setFiltroDataDe("");
    setFiltroDataAte("");
    // `ordenarPor` NÃO é limpo aqui: é visualização, não filtro — não conta no
    // badge "N filtros" nem some no "limpar".
    setFiltroTipo("");
    setFiltroModalidade("");
    setFiltroTag("");
    setMostrarArquivadas(false);
  }

  // Mesma ordenação da visão Kanban, aplicada à visão Lista (que não tem
  // colunas). Sem isso, trocar pra Lista ignorava o "Ordenar por".
  const filtradasOrdenadas = useMemo(
    () => (ordenarPor ? [...filtradas].sort((a, b) => compararTarefas(a, b, ordenarPor)) : filtradas),
    [filtradas, ordenarPor],
  );

  const porColuna = useMemo(() => {
    const acc: Record<string, Tarefa[]> = {};
    for (const c of colunas) acc[c.id] = [];
    for (const t of filtradas) {
      if (!acc[t.status]) acc[t.status] = [];
      acc[t.status].push(t);
    }
    // Ordenação de visualização: reordena a CÓPIA em memória, dentro de cada
    // coluna. Não chama a API e não toca em `ordemKanban` — voltar pra "Manual"
    // devolve exatamente o arranjo que a pessoa tinha montado.
    if (ordenarPor) {
      for (const id of Object.keys(acc)) {
        acc[id] = [...acc[id]].sort((a, b) => compararTarefas(a, b, ordenarPor));
      }
    }
    return acc;
  }, [filtradas, colunas, ordenarPor]);

  return (
    // Em md+ vira coluna de altura fixa: cabeçalho/filtros ficam parados e o quadro
    // ocupa o resto — assim a página não rola, só as colunas por dentro.
    <div className="space-y-4 md:flex md:flex-col md:flex-1 md:min-h-0">
      <div className="flex items-center justify-between md:shrink-0">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">
            {internoMode === "internos" ? "Tarefas — Projetos Internos" : "Tarefas"}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {filtradas.length} de {tarefas.length} tarefa{tarefas.length !== 1 ? "s" : ""}
            {filtrosAtivos > 0 && (
              <button
                onClick={limparFiltros}
                className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                <X size={11} /> limpar {filtrosAtivos} filtro{filtrosAtivos !== 1 ? "s" : ""}
              </button>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Atalho pra "Minha tarefa atual" — só aparece quando há foco marcado.
              Clicar abre o drawer da tarefa via deep-link em ?tarefa=<id>. */}
          {me?.tarefaAtual && (
            <button
              type="button"
              onClick={() => setDrawerId(me.tarefaAtual!.id)}
              className="flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 px-3 py-2 rounded-lg text-sm font-medium max-w-[260px]"
              title={`Trabalhando em: ${me.tarefaAtual.titulo}`}
            >
              <Focus size={14} className="shrink-0" />
              <span className="truncate">{me.tarefaAtual.titulo}</span>
            </button>
          )}
          <button
            onClick={() => setShowNova(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Plus size={16} /> Nova tarefa
          </button>
          <button
            onClick={() => setShowConfigNotif(true)}
            className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-3 py-2 rounded-lg text-sm font-medium"
            title="Configurar notificações de tarefas"
          >
            <Settings size={16} />
          </button>
        </div>
      </div>

      {/* Barra de controle sempre visível: busca + botão Filtros + vista.
          Os filtros ficam no painel recolhível abaixo (pedido da Rec, jul/2026). */}
      <div className="flex flex-wrap gap-2 items-center md:shrink-0">
        <div className="flex-1 min-w-[220px] relative">
          <SearchInput value={busca} onChange={setBusca} placeholder="Buscar — texto, ou #tag (ex.: #podcast)" />
          {/* Sugestão de tags quando o usuário digita # — clica pra inserir */}
          {(() => {
            const m = busca.match(/(?:^|\s)#([^\s#]+)$/);
            if (!m || !m[1]) return null;
            const fragmento = m[1].toLowerCase();
            const sugeridas = tagsCatalog
              .filter((tg) => tg.slug.includes(fragmento) || tg.nome.toLowerCase().includes(fragmento))
              .slice(0, 6);
            if (sugeridas.length === 0) return null;
            return (
              <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-1 flex flex-wrap gap-1">
                {sugeridas.map((tg) => (
                  <button
                    key={tg.id}
                    type="button"
                    onClick={() => {
                      // Substitui o último token #parcial pelo #slug completo
                      const novo = busca.replace(/(?:^|\s)#[^\s#]+$/, (match) =>
                        match.startsWith(" ") ? ` #${tg.slug}` : `#${tg.slug}`,
                      ) + " ";
                      setBusca(novo);
                    }}
                    className="text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 hover:opacity-80"
                    style={{ background: tg.cor, color: "#fff" }}
                  >
                    <span>{tg.icone}</span>
                    <span>{tg.nome}</span>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
        <button
          type="button"
          onClick={toggleFiltros}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            showFiltros || filtrosAtivos > 0
              ? "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300"
              : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
          }`}
          title="Mostrar ou ocultar os filtros"
        >
          <SlidersHorizontal size={15} />
          Filtros
          {filtrosAtivos > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold">
              {filtrosAtivos}
            </span>
          )}
          <ChevronDown size={14} className={`transition-transform ${showFiltros ? "rotate-180" : ""}`} />
        </button>

        {filtrosAtivos > 0 && (
          <button
            type="button"
            onClick={limparFiltros}
            className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            <X size={12} /> limpar
          </button>
        )}

        {/* MELHORIA 24 — liga/desliga os detalhes na capa do card (por pessoa). */}
        <button
          type="button"
          onClick={() => setDetalhesCards((v) => !v)}
          className={`ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            detalhesCards
              ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              : "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300"
          }`}
          title={detalhesCards ? "Ocultar os detalhes na capa dos cards (deixar enxuto)" : "Mostrar os detalhes na capa dos cards"}
        >
          {detalhesCards ? <Eye size={15} /> : <EyeOff size={15} />}
          Detalhes
        </button>

        {/* Vista Kanban | Lista — Viz #1 (Calendário e Gantt já existem em outras telas). */}
        <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
          <button
            type="button"
            onClick={() => setVista("kanban")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              vista === "kanban" ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            <Columns3 size={14} /> Kanban
          </button>
          <button
            type="button"
            onClick={() => setVista("lista")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              vista === "lista" ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            <ListIcon size={14} /> Lista
          </button>
        </div>
      </div>

      {/* Painel de filtros — recolhível pelo botão "Filtros" acima */}
      {showFiltros && (
        <div className="space-y-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40 p-3 md:shrink-0">
          {/* Toggle Todos | Externos | Internos. Suporte fica travado em internos. */}
          {!ehSuporte && can(["gestor", "admin", "funcionario"]) && (
            <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
              <button
                type="button"
                onClick={() => setInternoMode("todos")}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  internoMode === "todos"
                    ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                Todos
              </button>
              <button
                type="button"
                onClick={() => setInternoMode("externos")}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  internoMode === "externos"
                    ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                Externos
              </button>
              <button
                type="button"
                onClick={() => setInternoMode("internos")}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  internoMode === "internos"
                    ? "bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-300 shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                <Building2 size={14} /> Internos
              </button>
            </div>
          )}

          {/* Selects + escopo Minhas | Todas */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="min-w-[220px]">
              <SearchableSelect
                items={projetos}
                value={filtroProjetoId}
                onChange={setFiltroProjetoId}
                getId={(p) => p.id}
                // Mesmo rótulo do seletor de criar tarefa: projeto + cliente.
                getLabel={(p) => rotuloProjeto(p)}
                placeholder="Todos os projetos"
              />
            </div>
            {/* Pessoas e tipos aceitam VÁRIOS (pedido da Thainá, jul/2026). */}
            <MultiSelectFiltro
              className="min-w-[200px]"
              items={usuariosAtivos.map((u: any) => ({ id: u.id, label: u.nome, secundario: u.email }))}
              values={filtroAtribuidos}
              onChange={setFiltroAtribuidos}
              placeholder="Todos os responsáveis"
              nomePlural="pessoas"
            />
            <MultiSelectFiltro
              className="min-w-[190px]"
              items={(tiposTarefa as any[]).filter((t) => t.usadoComoTipo).map((t) => ({ id: t.id, label: t.label }))}
              values={filtroTiposTarefa}
              onChange={setFiltroTiposTarefa}
              placeholder="Todos os tipos de tarefa"
              nomePlural="tipos"
            />
            <MultiSelectFiltro
              className="min-w-[180px]"
              items={(clientes as any[]).map((c) => ({ id: c.id, label: c.nome }))}
              values={filtroClientes}
              onChange={setFiltroClientes}
              placeholder="Todos os clientes"
              nomePlural="clientes"
            />
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 text-sm outline-none focus:border-blue-500"
              title="Categoria do PROJETO (não confundir com o tipo da tarefa)"
            >
              <option value="">Todas as categorias de projeto</option>
              {tiposProjeto.map((t) => (
                <option key={t.id} value={t.slug}>{t.label}</option>
              ))}
            </select>
            {/* MELHORIA 14 — modalidade (avulso/mensal) do projeto. */}
            <select
              value={filtroModalidade}
              onChange={(e) => setFiltroModalidade(e.target.value)}
              className="px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 text-sm outline-none focus:border-blue-500"
              title="Modalidade do projeto (como é cobrado)"
            >
              <option value="">Avulso e mensal</option>
              <option value="avulso">Só avulso</option>
              <option value="mensal">Só mensal</option>
            </select>
            {/* Período pelo PRAZO da tarefa. */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800">
              <Calendar size={14} className="text-slate-400 shrink-0" />
              <input
                type="date"
                value={filtroDataDe}
                onChange={(e) => setFiltroDataDe(e.target.value)}
                className="bg-transparent text-sm outline-none dark:text-slate-100 w-[125px]"
                title="Prazo a partir de"
              />
              <span className="text-slate-400 text-xs">até</span>
              <input
                type="date"
                value={filtroDataAte}
                onChange={(e) => setFiltroDataAte(e.target.value)}
                className="bg-transparent text-sm outline-none dark:text-slate-100 w-[125px]"
                title="Prazo até"
              />
              {(filtroDataDe || filtroDataAte) && (
                <button
                  type="button"
                  onClick={() => { setFiltroDataDe(""); setFiltroDataAte(""); }}
                  className="text-slate-400 hover:text-red-600 shrink-0"
                  title="Limpar período"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            {(tagsCatalog.length > 0 || todasTags.length > 0) && (
              <select
                value={filtroTag}
                onChange={(e) => setFiltroTag(e.target.value)}
                className="px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 text-sm outline-none focus:border-blue-500"
              >
                <option value="">Todas as tags</option>
                {/* Catálogo cadastrado primeiro (com ícone). Depois, slugs órfãos
                    que estão em tarefas mas não no catálogo (legado de antes). */}
                {tagsCatalog.map((tg) => (
                  <option key={tg.id} value={tg.slug}>{tg.icone} {tg.nome}</option>
                ))}
                {todasTags
                  .filter((slug) => !tagsCatalog.some((tg) => tg.slug === slug))
                  .map((slug) => (
                    <option key={slug} value={slug}>#{slug}</option>
                  ))}
              </select>
            )}

            <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
              <button
                type="button"
                onClick={() => trocarEscopo("minhas")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  escopo === "minhas"
                    ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                Minhas
              </button>
              <button
                type="button"
                onClick={() => trocarEscopo("todas")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  escopo === "todas"
                    ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                Todas
              </button>
            </div>
          </div>

          {/* Chips clicáveis de filtro rápido */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <FiltroChip
              ativo={apenasProprias}
              onClick={() => setApenasProprias((v) => !v)}
              icon={<UserIcon size={12} />}
              cor="blue"
            >
              Próprias (atribuídas a mim)
            </FiltroChip>
            <FiltroChip
              ativo={apenasAvulsas}
              onClick={() => setApenasAvulsas((v) => !v)}
              icon={<FileMinus size={12} />}
              cor="slate"
            >
              Avulsas (sem projeto)
            </FiltroChip>
            <FiltroChip
              ativo={apenasInternas}
              onClick={() => setApenasInternas((v) => !v)}
              icon={<EyeOff size={12} />}
              cor="violet"
            >
              Internas (cliente não vê)
            </FiltroChip>
            <FiltroChip
              ativo={apenasVisiveisCliente}
              onClick={() => setApenasVisiveisCliente((v) => !v)}
              icon={<Eye size={12} />}
              cor="emerald"
            >
              Visíveis ao cliente
            </FiltroChip>
            <FiltroChip
              ativo={apenasAtrasadas}
              onClick={() => setApenasAtrasadas((v) => !v)}
              icon={<AlertTriangle size={12} />}
              cor="red"
            >
              Atrasadas
            </FiltroChip>
            <FiltroChip
              ativo={apenasVence24h}
              onClick={() => setApenasVence24h((v) => !v)}
              icon={<Clock size={12} />}
              cor="amber"
            >
              Vencem em 24h
            </FiltroChip>
            {/* Recorte por coluna (deep-link ?status=). Pill separada dos chips
                toggle porque é um valor único (não liga/desliga um booleano). */}
            {filtroColuna && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border border-violet-300 dark:border-violet-700">
                Só: {colunas.find((c) => c.id === filtroColuna)?.titulo || filtroColuna}
                <button
                  type="button"
                  onClick={() => setFiltroColuna("")}
                  className="hover:text-red-600 dark:hover:text-red-400"
                  title="Limpar recorte de coluna"
                  aria-label="Limpar recorte de coluna"
                >
                  <X size={12} />
                </button>
              </span>
            )}
            <FiltroChip
              ativo={mostrarArquivadas}
              onClick={() => setMostrarArquivadas((v) => !v)}
              icon={<Archive size={12} />}
              cor="slate"
            >
              Arquivadas
            </FiltroChip>
          </div>

          {/* ORDENAR — não é filtro (não entra no badge nem no "limpar"): muda só
              como os cards aparecem dentro da coluna. Fica aqui embaixo, separado
              por uma linha, pra não se misturar com os filtros de verdade. */}
          <div className="pt-2.5 border-t border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-2">
            <label className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5">
              <ArrowDownUp size={13} /> Ordenar cards por
            </label>
            <select
              value={ordenarPor}
              onChange={(e) => setOrdenarPor(e.target.value as OrdenacaoKanban)}
              className={
                "px-3 py-1.5 rounded-lg border text-sm outline-none focus:border-blue-500 " +
                (ordenarPor
                  ? "border-blue-400 bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200 dark:border-blue-700"
                  : "border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100")
              }
            >
              {ORDENACOES.map((o) => (
                <option key={o.valor} value={o.valor}>{o.label}</option>
              ))}
            </select>
            {ordenarPor && (
              <span className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-2 py-1">
                Só muda a exibição — sua ordem manual está guardada. Enquanto isso,
                arrastar <b>entre colunas</b> funciona; reordenar <b>dentro</b> da coluna, não.
              </span>
            )}
          </div>
        </div>
      )}

      {vista === "lista" ? (
        // A lista precisa rolar; ganha rolagem PRÓPRIA pra página seguir sem a barra principal.
        <div className="md:flex-1 md:min-h-0 md:overflow-y-auto">
          <ListaTarefas tarefas={filtradasOrdenadas} colunas={colunas} onAbrir={setDrawerId} />
        </div>
      ) : (
      <>
      <KanbanTopScrollbar targetRef={boardRef} />
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="__board__" direction="horizontal" type="COLUMN">
          {(provBoard) => (
            // ÚNICO container de rolagem do quadro (X e Y) — é isso que deixa a lib
            // de drag fazer o auto-scroll ao arrastar o card sem perder as medidas.
            // As colunas NÃO podem voltar a rolar por dentro sem que o auto-scroll
            // lateral saia junto (ver o CSS de .kanban-board-scroll).
            // min-h de 300px é a rede de segurança: numa janela muito baixa o quadro
            // não vira um filete — a página volta a rolar (a barra reaparece só aí).
            <div
              ref={(el) => { boardRef.current = el; }}
              onMouseDown={pan.onMouseDown}
              className="overflow-auto kanban-board-scroll md:flex-1 md:min-h-[300px]"
            >
            {/* Linha das colunas SEPARADA do container de rolagem, de propósito:
                como a altura dela é o conteúdo (a coluna mais alta), o items-stretch
                estica TODAS as colunas até essa altura. Sem isso, uma coluna curta
                acabava na altura da tela visível e, lá no fundo de uma coluna longa,
                não existia área nenhuma pra soltar o card — a pessoa tinha que subir
                o card antes de ir pro lado (relato do Ricardo, jul/2026). É isso que
                também faz o cabeçalho sticky valer a pena: agora ele gruda no topo
                de uma coluna que existe até embaixo. */}
            <div
              ref={provBoard.innerRef}
              {...provBoard.droppableProps}
              // `w-max` (hugga o conteúdo, encosta à esquerda) no quadro normal
              // pra a rolagem lateral funcionar. No recorte de UMA coluna não há
              // o que rolar, então usa `w-full justify-center` pra a coluna
              // sozinha ficar no MEIO (pedido do Ricardo) em vez de no canto.
              className={
                "flex gap-3 items-stretch min-h-full " +
                (filtroColuna ? "w-full justify-center" : "w-max")
              }
            >
          {/* Com recorte por coluna (deep-link ?status=), mostra SÓ aquela
              coluna — as outras estão vazias mesmo, e assim ela aparece na hora
              sem precisar rolar até achar (pedido do Ricardo, jul/2026). O X da
              pill "Só: …" devolve todas. */}
          {(filtroColuna ? colunas.filter((c) => c.id === filtroColuna) : colunas).map((col, colIdx) => (
            <Draggable draggableId={`__col__${col.id}`} index={colIdx} key={col.id}>
              {(provCol) => (
            // A altura vem do items-stretch da linha (= a coluna mais alta), então
            // toda coluna tem área de drop em qualquer profundidade da rolagem.
            // min-h-[400px] segue valendo no mobile, onde não há altura fixa.
            <div ref={provCol.innerRef} {...provCol.draggableProps} className="bg-slate-100 dark:bg-slate-800/60 rounded-xl p-2 flex flex-col min-h-[400px] md:min-h-0 w-[270px] shrink-0">
              {/* sticky: o quadro rola na vertical, então o título/contador gruda no
                  topo em vez de sumir junto com os cards. Fundo OPACO de propósito —
                  com o dark:bg-slate-800/60 da coluna, os cards passariam por baixo
                  e apareceriam através do cabeçalho. */}
              <div className="flex items-center justify-between px-2 py-1 mb-2 sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <h3 {...provCol.dragHandleProps} className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5 cursor-grab active:cursor-grabbing min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: col.cor || "#94a3b8" }} />
                  <span className="truncate">{col.titulo}</span>
                </h3>
                <div className="flex items-center gap-1 shrink-0">
                  <label className="cursor-pointer text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400 relative w-4 h-4 inline-flex items-center justify-center" title="Cor da coluna (só pra você)">
                    <Palette size={13} />
                    <input
                      type="color"
                      value={col.cor || "#94a3b8"}
                      onChange={(e) => atualizarPrefs({ ...colPrefs, cores: { ...colPrefs.cores, [col.id]: e.target.value } })}
                      className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                    />
                  </label>
                  {colPrefs.cores[col.id] && (
                    <button
                      onClick={() => { const c = { ...colPrefs.cores }; delete c[col.id]; atualizarPrefs({ ...colPrefs, cores: c }); }}
                      className="text-slate-300 hover:text-red-500"
                      title="Resetar cor"
                    >
                      <X size={11} />
                    </button>
                  )}
                  <span className="text-xs text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-700 rounded-full px-2 py-0.5">
                    {porColuna[col.id]?.length || 0}
                  </span>
                </div>
              </div>
              <Droppable droppableId={col.id} type="CARD">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    // SEM rolagem própria de propósito: quem rola é o quadro. Uma
                    // rolagem aqui seria aninhada e mataria o auto-scroll do card.
                    // flex-1 mantém a área de drop ocupando a coluna toda, inclusive
                    // quando ela está vazia.
                    className="flex-1 space-y-2 transition-colors rounded-lg p-1"
                    // Passando o card por cima, a coluna acende NA COR DELA (antes era
                    // um azul fixo, igual pra todas). Inline porque a cor é dinâmica
                    // (escolhida no seletor de cor da coluna) — Tailwind não alcança.
                    style={
                      snapshot.isDraggingOver
                        ? {
                            backgroundColor: hexParaRgba(col.cor || COR_COLUNA_PADRAO, 0.16),
                            boxShadow: `inset 0 0 0 2px ${hexParaRgba(col.cor || COR_COLUNA_PADRAO, 0.7)}`,
                          }
                        : undefined
                    }
                  >
                    {porColuna[col.id]?.map((t, index) => {
                      const urg = urgenciaTarefa(t);
                      const dias = diasRestantes(t.prazo);
                      const subOk = (t.subitens || []).filter((s) => s.concluido).length;
                      const subTotal = (t.subitens || []).length;
                      return (
                        <Draggable key={t.id} draggableId={t.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setDrawerId(t.id)}
                              className={`bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 cursor-pointer hover:shadow-md transition-shadow overflow-hidden ${
                                snapshot.isDragging ? "shadow-xl rotate-1" : ""
                              } ${t.arquivada ? "opacity-60" : ""}`}
                            >
                              <div className={`h-1 ${urg.bg}`} />
                              <div className="p-3">
                                <div className="flex items-start justify-between gap-2">
                                  <p className={`text-sm font-medium line-clamp-2 ${t.arquivada ? "text-slate-500 dark:text-slate-500 line-through" : "text-slate-800 dark:text-slate-100"}`}>{t.titulo}</p>
                                  {t.arquivada ? (
                                    <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded shrink-0 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">arquivada</span>
                                  ) : (
                                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded shrink-0 ${urg.badge}`}>
                                      {urg.texto}
                                    </span>
                                  )}
                                </div>
                                {/* MELHORIA 18 — tipo de tarefa visível na capa (ícone + nome, na cor do tipo). */}
                                {detalhesCards && t.tipoTarefa && (
                                  <span
                                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded text-white max-w-full"
                                    style={{ background: t.tipoTarefa.cor || "#64748b" }}
                                    title={`Tipo de tarefa: ${t.tipoTarefa.label}`}
                                  >
                                    <span className="text-xs leading-none shrink-0">{t.tipoTarefa.icone}</span>
                                    <span className="truncate">{t.tipoTarefa.label}</span>
                                  </span>
                                )}
                                {detalhesCards && t.projeto && (
                                  <div className="mt-1 max-w-full">
                                    <TipoProjetoChip
                                      tipo={tiposProjeto.find((tp) => tp.slug === t.projeto?.tipo)}
                                      nome={t.projeto.nome}
                                      fotoUrl={t.projeto.fotoUrl}
                                      tamanho="xs"
                                    />
                                    {t.projeto.clienteRef?.nome && (
                                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate flex items-center gap-1">
                                        <Building2 size={10} className="shrink-0" /> {t.projeto.clienteRef.nome}
                                      </p>
                                    )}
                                  </div>
                                )}
                                {detalhesCards && <SeloCobrancaCard status={t.status} statusDesde={(t as any).statusDesde} ultimaCobrancaEm={(t as any).ultimaCobrancaEm} />}
                                <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500 dark:text-slate-400 flex-wrap">
                                  {cardEdit?.id === t.id && cardEdit.campo === "data" ? (
                                    <QuickEditData
                                      valorInicial={ymdLocal(t.prazo)}
                                      salvando={quickEditM.isPending}
                                      onSalvar={(ymd) => { quickEditM.mutate({ id: t.id, dados: { prazo: `${ymd}T12:00:00` } }); setCardEdit(null); }}
                                      onCancelar={() => setCardEdit(null)}
                                    />
                                  ) : (
                                    <span
                                      className={`flex items-center gap-1 ${t.arquivada ? "" : "cursor-pointer hover:text-blue-600"}`}
                                      onClick={(e) => { e.stopPropagation(); if (!t.arquivada) setCardEdit({ id: t.id, campo: "data" }); }}
                                      onMouseDown={(e) => { if (!t.arquivada) e.stopPropagation(); }}
                                      title={t.arquivada ? undefined : "Clique para alterar a data"}
                                    >
                                      <Calendar size={11} />
                                      {formatarPrazo(t)}
                                      {tarefaPodeVencer(t.status) && (
                                        <span className={dias < 0 ? "text-red-600 font-semibold" : ""}>
                                          {dias < 0 ? ` (${-dias}d atrasada)` : dias === 0 ? " (hoje)" : ` (${dias}d)`}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                  {/* MELHORIA 16 — data combinada com o cliente, chip roxo bem
                                      visível na capa; só gestor/admin vê (funcionário nem recebe o campo). */}
                                  {detalhesCards && can(["gestor", "admin"]) && (t as any).dataEntregaCliente && (
                                    <span className="flex items-center gap-1 font-bold text-purple-700 bg-purple-100 dark:bg-purple-900/40 dark:text-purple-300 px-1.5 py-0.5 rounded" title="Entrega combinada com o cliente — só gestão vê">
                                      🔒 Cliente {new Date((t as any).dataEntregaCliente).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                                    </span>
                                  )}
                                  {detalhesCards && t.recorrencia && (
                                    <span className="flex items-center gap-0.5" title={`Recorrência ${t.recorrencia}`}>
                                      <Repeat size={11} />
                                    </span>
                                  )}
                                  {/* MELHORIA 15 — peças entregues, bem visível quando é mais de uma. */}
                                  {detalhesCards && ((t as any).quantidadePecas ?? 1) > 1 && (
                                    <span className="flex items-center gap-0.5 font-bold text-indigo-700 bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 px-1.5 py-0.5 rounded" title="Quantidade de peças entregues neste card">
                                      <Package size={11} /> {(t as any).quantidadePecas} peças
                                    </span>
                                  )}
                                  {detalhesCards && t.aprovadorId && (
                                    <span className="flex items-center gap-0.5" title="Requer aprovação">
                                      <ShieldCheck size={11} />
                                    </span>
                                  )}
                                  {detalhesCards && (t.anexos?.length || 0) > 0 && (
                                    <span className="flex items-center gap-0.5">
                                      <Paperclip size={11} /> {t.anexos!.length}
                                    </span>
                                  )}
                                  {detalhesCards && subTotal > 0 && (
                                    <span className="flex items-center gap-0.5">
                                      <ListChecks size={11} /> {subOk}/{subTotal}
                                    </span>
                                  )}
                                </div>
                                {detalhesCards && t.etiquetas && t.etiquetas.trim() && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {t.etiquetas.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean).slice(0, 4).map((slug) => (
                                      <TagChip
                                        key={slug}
                                        tag={tagsCatalog.find((tg) => tg.slug === slug)}
                                        slug={slug}
                                        tamanho="xs"
                                      />
                                    ))}
                                  </div>
                                )}
                                <div className="flex items-center justify-between mt-2 gap-2">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    {/* SÓ AVATARES no card (pedido da Thainá, ago/2026):
                                        a fila mostra todo mundo que está na tarefa, o
                                        nome+função aparecem no tooltip e ao abrir o card.
                                        A 1ª bolinha é a principal (anel azul). */}
                                    <span className="relative inline-flex items-center min-w-0">
                                      {(() => {
                                        const equipe = [
                                          ...(t.atribuido
                                            ? [{ id: t.atribuido.id, nome: t.atribuido.nome, fotoUrl: (t.atribuido as any).fotoUrl as string | null, funcao: t.funcaoPrincipal || null, principal: true }]
                                            : []),
                                          ...(t.responsaveis || [])
                                            .filter((r) => r.usuarioId !== t.atribuidoA)
                                            .map((r) => ({ id: r.usuarioId, nome: r.usuario?.nome || "—", fotoUrl: r.usuario?.fotoUrl || null, funcao: r.funcao || null, principal: false })),
                                        ];
                                        const abrir = (e: React.MouseEvent) => {
                                          e.stopPropagation();
                                          if (t.arquivada) return;
                                          setAncoraPessoa(e.currentTarget.getBoundingClientRect());
                                          setCardEdit({ id: t.id, campo: "pessoa" });
                                        };
                                        if (equipe.length === 0) {
                                          return (
                                            <span onClick={abrir} onMouseDown={(e) => e.stopPropagation()}
                                              className="text-[11px] text-slate-400 italic cursor-pointer hover:text-blue-600">
                                              atribuir…
                                            </span>
                                          );
                                        }
                                        const rotulo = equipe
                                          .map((p) => p.nome + (p.funcao ? ` — ${p.funcao}` : "") + (p.principal ? " (principal)" : ""))
                                          .join("\n");
                                        return (
                                          <span
                                            className={`flex items-center ${t.arquivada ? "" : "cursor-pointer"}`}
                                            onClick={abrir}
                                            onMouseDown={(e) => { if (!t.arquivada) e.stopPropagation(); }}
                                            title={t.arquivada ? rotulo : rotulo + "\n\nClique para mudar quem faz"}
                                          >
                                            {/* -ml sobrepõe as bolinhas (pilha), como no Trello. */}
                                            {equipe.slice(0, 4).map((p, i) => (
                                              <Avatar
                                                key={p.id}
                                                nome={p.nome}
                                                fotoUrl={p.fotoUrl}
                                                tamanho={22}
                                                bgFallback={corDaPessoa(p.id)}
                                                className={`${i > 0 ? "-ml-1.5" : ""} ring-2 ${p.principal ? "ring-blue-500" : "ring-white dark:ring-slate-800"}`}
                                              />
                                            ))}
                                            {equipe.length > 4 && (
                                              <span className="-ml-1.5 w-[22px] h-[22px] rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-200 text-[9px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-slate-800 shrink-0">
                                                +{equipe.length - 4}
                                              </span>
                                            )}
                                          </span>
                                        );
                                      })()}
                                      {cardEdit?.id === t.id && cardEdit.campo === "pessoa" && (
                                        <QuickEditPessoas
                                          tarefa={t}
                                          usuarios={usuariosAtivos}
                                          salvando={quickEditM.isPending}
                                          ancora={ancoraPessoa}
                                          onSalvar={(dados) => { quickEditM.mutate({ id: t.id, dados }); setCardEdit(null); }}
                                          onFechar={() => setCardEdit(null)}
                                        />
                                      )}
                                    </span>
                                    {t.atribuido?.id === user?.id && (
                                      <span className="text-[9px] uppercase font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded shrink-0">
                                        sua
                                      </span>
                                    )}
                                  </div>
                                  {/* Play/Pause direto no card — Cards #5. Só pra quem
                                      atua na tarefa (atribuído/co-responsável) ou gestão. */}
                                  {!t.arquivada && t.status !== "concluido" && (() => {
                                    const tocando = me?.tarefaAtualId === t.id;
                                    const podePlay =
                                      t.atribuidoA === user?.id ||
                                      (t.responsaveis?.some((r) => r.usuarioId === user?.id) ?? false) ||
                                      can(["gestor", "admin"]);
                                    if (!podePlay) return null;
                                    return (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); playM.mutate(tocando ? null : t.id); }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        disabled={playM.isPending}
                                        title={tocando ? "Pausar play (estou nessa)" : "Dar play (estou nessa)"}
                                        className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors disabled:opacity-50 ${
                                          tocando
                                            ? "bg-emerald-500 text-white hover:bg-emerald-600 animate-pulse"
                                            : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 hover:bg-emerald-100 hover:text-emerald-600 dark:hover:bg-emerald-900/40"
                                        }`}
                                      >
                                        {tocando ? <Pause size={13} /> : <Play size={13} />}
                                      </button>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                    {(porColuna[col.id]?.length || 0) === 0 && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-6">Nenhuma tarefa</p>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
              )}
            </Draggable>
          ))}
              {provBoard.placeholder}
            </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>
      </>
      )}

      {drawerId && <TarefaDrawer tarefaId={drawerId} onDuplicada={(novaId) => setDrawerId(novaId)} onClose={() => {
        setDrawerId(null);
        if (searchParams.has("tarefa")) {
          searchParams.delete("tarefa");
          setSearchParams(searchParams, { replace: true });
        }
      }} />}
      {showNova && <NovaTarefaModal onClose={() => setShowNova(false)} />}
      {showConfigNotif && <ConfigNotificacoesTarefasModal onClose={() => setShowConfigNotif(false)} />}
      {ajustePendente && (
        <RegistrarAjusteModal
          onConfirmar={confirmarAjuste}
          onCancelar={() => setAjustePendente(null)}
          salvando={salvandoAjuste}
          tarefaId={ajustePendente.id}
        />
      )}
    </div>
  );
}

// Vista em LISTA (Viz #1) — tabela das tarefas filtradas, clicáveis pra abrir o drawer.
function ListaTarefas({ tarefas, colunas, onAbrir }: {
  tarefas: Tarefa[];
  colunas: Array<{ id: string; titulo: string; cor?: string | null }>;
  onAbrir: (id: string) => void;
}) {
  const statusLabel = (s: string) => colunas.find((c) => c.id === s)?.titulo || s;
  if (tarefas.length === 0) return <p className="text-sm text-slate-400 py-8 text-center">Nenhuma tarefa.</p>;
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Tarefa</th>
            <th className="text-left px-4 py-2 font-medium">Projeto / Cliente</th>
            <th className="text-left px-4 py-2 font-medium">Responsável</th>
            <th className="text-left px-4 py-2 font-medium">Status</th>
            <th className="text-left px-4 py-2 font-medium">Prazo</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {tarefas.map((t) => {
            const dias = diasRestantes(t.prazo);
            return (
              <tr key={t.id} onClick={() => onAbrir(t.id)} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer">
                <td className="px-4 py-2">
                  <span className={`font-medium ${t.arquivada ? "line-through text-slate-400" : "text-slate-800 dark:text-slate-100"}`}>{t.titulo}</span>
                </td>
                <td className="px-4 py-2 text-slate-600 dark:text-slate-300">
                  {t.projeto?.nome || <span className="text-slate-400">Avulsa</span>}
                  {t.projeto?.clienteRef?.nome && <span className="text-slate-400"> · {t.projeto.clienteRef.nome}</span>}
                </td>
                <td className="px-4 py-2 text-slate-600 dark:text-slate-300">{t.atribuido?.nome || "—"}</td>
                <td className="px-4 py-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{statusLabel(t.status)}</span>
                </td>
                <td className="px-4 py-2 tabular-nums text-slate-600 dark:text-slate-300">
                  {formatarPrazo(t)}
                  {tarefaPodeVencer(t.status) && (
                    <span className={dias < 0 ? "text-red-600 font-semibold ml-1" : "text-slate-400 ml-1"}>
                      {dias < 0 ? `(${-dias}d)` : dias === 0 ? "(hoje)" : `(${dias}d)`}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type ChipCor = "slate" | "violet" | "emerald" | "red" | "blue" | "amber";

const CHIP_CLASSES_ATIVO: Record<ChipCor, string> = {
  slate:   "bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600",
  violet:  "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700",
  emerald: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700",
  red:     "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700",
  blue:    "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700",
  amber:   "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700",
};

function FiltroChip({ ativo, onClick, icon, children, cor = "blue" }: {
  ativo: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
  cor?: ChipCor;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors ${
        ativo
          ? CHIP_CLASSES_ATIVO[cor]
          : "bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-700 dark:hover:text-slate-200"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

// =============================================================================
// Config de notificações de tarefas
// =============================================================================
function ConfigNotificacoesTarefasModal({ onClose }: { onClose: () => void }) {
  const [ativo, setAtivo] = useState(getTarefasLembreteAtivo());
  const [antecedencia, setAntecedencia] = useState(getTarefasAntecedencia());
  const [repeticoes, setRepeticoes] = useState(getTarefasRepeticoes());
  const [intervalo, setIntervalo] = useState(getTarefasIntervalo());
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );

  function toggleAtivo(v: boolean) {
    setAtivo(v);
    setTarefasLembreteAtivo(v);
    if (v && permission !== "granted" && typeof Notification !== "undefined") {
      Notification.requestPermission().then((p) => setPermission(p));
    }
  }
  function pedirPermissao() {
    if (typeof Notification === "undefined") return;
    Notification.requestPermission().then((p) => setPermission(p));
  }

  const semSuporte = typeof Notification === "undefined";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Settings size={16} /> Notificações de tarefas
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <Bell size={18} className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Lembretes do prazo</p>
              <p className="text-xs text-blue-700/80 dark:text-blue-300/80">
                Avisos antes do prazo das suas tarefas pendentes (no navegador e no sininho).
              </p>
            </div>
          </div>

          {semSuporte && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 px-3 py-2 rounded text-xs">
              Seu navegador não suporta notificações.
            </div>
          )}

          <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30">
            <input
              type="checkbox"
              checked={ativo}
              onChange={(e) => toggleAtivo(e.target.checked)}
              disabled={semSuporte}
              className="mt-0.5 accent-blue-600"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Ativar notificações</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Por padrão desativado. Quando ligado, recebe avisos antes do prazo.
              </p>
            </div>
          </label>

          <div className={`space-y-3 ${ativo ? "" : "opacity-50 pointer-events-none"}`}>
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-400 block mb-1">Avisar quanto tempo antes</label>
              <select
                value={antecedencia}
                onChange={(e) => { const v = Number(e.target.value); setAntecedencia(v); setTarefasAntecedencia(v); }}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:border-blue-500 outline-none"
              >
                {TAREFA_ANTECEDENCIA_OPCOES.map((o) => (
                  <option key={o.valor} value={o.valor}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-400 block mb-1">Quantas vezes</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={repeticoes}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1));
                    setRepeticoes(v);
                    setTarefasRepeticoes(v);
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:border-blue-500 outline-none tabular-nums"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-400 block mb-1">Intervalo entre avisos</label>
                <select
                  value={intervalo}
                  onChange={(e) => { const v = Number(e.target.value); setIntervalo(v); setTarefasIntervalo(v); }}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:border-blue-500 outline-none"
                >
                  {TAREFA_INTERVALO_OPCOES.map((o) => (
                    <option key={o.valor} value={o.valor}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Exemplo: avisar <strong>{TAREFA_ANTECEDENCIA_OPCOES.find((o) => o.valor === antecedencia)?.label}</strong>,
              repetindo até <strong>{repeticoes}x</strong> com intervalo de <strong>{intervalo} min</strong>.
            </p>
          </div>

          {!semSuporte && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Permissão do navegador:{" "}
                <strong className={
                  permission === "granted" ? "text-emerald-600 dark:text-emerald-400" :
                  permission === "denied" ? "text-red-600 dark:text-red-400" :
                  "text-amber-600 dark:text-amber-400"
                }>
                  {permission === "granted" ? "permitido" : permission === "denied" ? "bloqueado" : "ainda não solicitado"}
                </strong>
              </p>
              {permission !== "granted" && (
                <button
                  type="button"
                  onClick={pedirPermissao}
                  disabled={permission === "denied"}
                  className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
                >
                  {permission === "denied" ? "Bloqueado pelo navegador" : "Permitir notificações"}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-1.5 rounded text-sm bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
